import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { api, imageUrl } from "@/lib/api";

const lineAnim = (delay) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] },
});

const LOCATIONS = ["India", "UAE", "Nepal", "Global"];

const Hero = () => {
  const ref = useRef(null);
  const [profile, setProfile] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden hero-grid-bg" data-testid="hero-section">
      <div className="aurora -top-40 -right-24 h-[480px] w-[480px] bg-[#38BDF8]/15" />
      <div className="aurora bottom-[-80px] -left-32 h-[440px] w-[440px] bg-[#E9C88A]/14" />
      <div className="aurora top-1/3 left-[38%] h-[300px] w-[300px] bg-[#10B981]/8" />
      <motion.div style={{ y: yBg, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8 pt-28 pb-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="flex flex-wrap items-center gap-2 mb-8">
          {LOCATIONS.map((loc, i) => (
            <span key={loc} data-testid={`hero-location-${loc.toLowerCase()}`} className="animate-float-slow inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300" style={{ animationDelay: `${i * 0.7}s` }}>
              <MapPin size={11} className="text-[#E9C88A]" /> {loc}
            </span>
          ))}
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-center">
          <div>
            <h1 className="font-display font-semibold uppercase leading-[0.95] tracking-tight" data-testid="hero-title">
              <span className="mask-line"><motion.span className="block text-5xl sm:text-7xl lg:text-[6.5rem] text-slate-100" {...lineAnim(0.35)}>Bishwa</motion.span></span>
              <span className="mask-line"><motion.span className="block text-5xl sm:text-7xl lg:text-[6.5rem] gold-text" {...lineAnim(0.55)}>Mohan Pati</motion.span></span>
            </h1>
            <span className="mask-line mt-8"><motion.p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" {...lineAnim(0.8)}>
              Entrepreneur <span className="text-[#E9C88A]">|</span> Technology Builder <span className="text-[#E9C88A]">|</span> Business Strategist
            </motion.p></span>
            <span className="mask-line mt-2"><motion.p className="font-display italic text-2xl md:text-3xl text-slate-400" {...lineAnim(0.95)}>
              Building businesses. Connecting markets. Creating meaningful impact.
            </motion.p></span>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => scrollToId("journey")} data-testid="hero-explore-button" className="rounded-full bg-[#E9C88A] px-7 py-3.5 text-sm font-semibold text-[#0A0D14] hover:bg-[#f5dda9] hover:shadow-[0_10px_36px_rgba(233,200,138,0.35)] transition-all duration-300">
                Explore My Work
              </button>
              <button onClick={() => scrollToId("impact")} data-testid="hero-conversation-button" className="rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-slate-100 hover:border-[#E9C88A]/60 hover:text-[#E9C88A] transition-colors duration-300">
                Start a Conversation
              </button>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9 }} className="hidden lg:block">
            {profile?.portrait_url ? (
              <div className="relative ml-auto w-fit animate-float-slow" data-testid="hero-portrait">
                <div className="absolute -inset-3 rounded-3xl border border-[#E9C88A]/30 rotate-3" />
                <div className="absolute -inset-3 rounded-3xl border border-white/5 -rotate-2" />
                <div className="absolute -inset-8 rounded-full bg-[#E9C88A]/10 blur-3xl" />
                <img src={imageUrl(profile.portrait_url)} alt="Bishwa Mohan Pati" className="relative h-[380px] w-[300px] rounded-2xl object-cover border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]" />
                <div className="absolute -bottom-4 left-6 rounded-full border border-[#E9C88A]/40 bg-[#0A0D14] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E9C88A]">Founder & Builder</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
                {[["7+", "Industries"], ["4", "Regions"], ["1", "Belief"]].map(([n, l]) => (
                  <div key={l} className="bg-[#0D1118] px-5 py-6">
                    <div className="font-display text-3xl font-semibold text-[#E9C88A]">{n}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      <motion.button onClick={() => scrollToId("about")} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate-500 hover:text-[#E9C88A] transition-colors" data-testid="hero-scroll-indicator" aria-label="Scroll down">
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
